<template>
  <!-- Filter bar of one Backup restore tab. Emits a NEW filter object on every
       change (v-model); the table debounces the reload. -->
  <div class="bk-filters">
    <div class="bk-filters__row">
      <div class="bk-filters__search">
        <i class="fa-solid fa-magnifying-glass"></i>
        <b-form-input :value="value.search" size="sm" autocomplete="off" debounce="250"
                      :placeholder="$t(`backup.searchPlaceholder.${entity}`)"
                      @update="v => set('search', v)"/>
      </div>

      <b-form-input v-if="entity === 'trades'" :value="value.symbol" size="sm" class="bk-filters__symbol"
                    debounce="250" :placeholder="$t('backup.columns.symbol')"
                    @update="v => set('symbol', v)"/>

      <div v-if="hasManyOrganizations && entity !== 'log'" class="bk-filters__org">
        <organization-select-box :value="value.orgIds || []" multiple clearable
                                 :placeholder="$t('backup.filters.allOrganizations')"
                                 @input="v => set('orgIds', v)"/>
      </div>

      <div v-if="entity === 'log'" class="bk-filters__org">
        <user-select-box :value="value.userIds || []" multi
                         :placeholder="$t('backup.filters.allUsers')"
                         @input="v => set('userIds', v)"/>
      </div>

      <b-form-checkbox v-if="entity === 'clients'" switch class="bk-filters__switch"
                       :checked="value.onlyWithMissingItems === true"
                       @change="v => set('onlyWithMissingItems', v)">
        {{ $t('backup.filters.onlyWithMissingItems') }}
      </b-form-checkbox>

      <button v-if="isFiltered" type="button" class="bk-filters__reset" @click="reset">
        <i class="fa-solid fa-filter-circle-xmark"></i> {{ $t('backup.filters.clear') }}
      </button>
    </div>

    <!-- Date: one chip per day of the incident window + custom range -->
    <div class="bk-filters__row">
      <span class="bk-filters__label"><i class="fa-regular fa-calendar"></i></span>
      <div class="bk-chips">
        <button type="button" class="bk-chip" :class="{ active: !hasRange }" @click="setRange(null, null)">
          {{ $t('backup.filters.allDays') }}
        </button>
        <button v-for="d in days" :key="d.value" type="button" class="bk-chip"
                :class="{ active: isDay(d.value) }" @click="setRange(d.value, d.value)">
          {{ d.label }}
        </button>
        <button type="button" class="bk-chip" :class="{ active: customRange }" @click="customOpen = !customOpen">
          <i class="fa-solid fa-sliders"></i> {{ $t('backup.filters.custom') }}
        </button>
      </div>
      <div v-if="customOpen || customRange" class="bk-filters__range">
        <b-form-input type="date" size="sm" :value="range.from" :min="sinceDay" @change="v => setRange(v || null, range.until)"/>
        <i class="fa-solid fa-arrow-right"></i>
        <b-form-input type="date" size="sm" :value="range.until" :min="range.from || sinceDay" @change="v => setRange(range.from, v || null)"/>
      </div>
    </div>

    <!-- Status chips (with counts from /backup/info) -->
    <div v-if="entity !== 'log'" class="bk-filters__row">
      <span class="bk-filters__label">{{ $t('backup.filters.status') }}</span>
      <div class="bk-chips">
        <button type="button" class="bk-chip" :class="{ active: !(value.statuses || []).length }"
                @click="set('statuses', [])">
          {{ $t('backup.filters.allStatuses') }}
          <span v-if="countsTotal != null" class="bk-chip__n">{{ countsTotal }}</span>
        </button>
        <button v-for="s in statusOptions" :key="s" type="button"
                class="bk-chip bk-chip--status" :class="[`bk-chip--${s.toLowerCase()}`, { active: isOn('statuses', s) }]"
                @click="toggle('statuses', s)">
          {{ bkStatusLabel(s) }}
          <span v-if="counts && counts[s] != null" class="bk-chip__n">{{ counts[s] }}</span>
        </button>
      </div>

      <template v-if="entity === 'monetary'">
        <span class="bk-filters__sep"></span>
        <div class="bk-chips">
          <button v-for="t in monetaryTypes" :key="t" type="button" class="bk-chip"
                  :class="{ active: isOn('types', t) }" @click="toggle('types', t)">{{ bkTypeLabel(t) }}</button>
        </div>
        <span class="bk-filters__sep"></span>
        <div class="bk-chips">
          <button v-for="t in realTypes" :key="t" type="button" class="bk-chip"
                  :class="{ active: isOn('realTypes', t) }" @click="toggle('realTypes', t)">{{ t }}</button>
        </div>
      </template>

      <template v-if="entity === 'trades'">
        <span class="bk-filters__sep"></span>
        <div class="bk-chips">
          <button v-for="t in ['CLOSED', 'OPEN']" :key="t" type="button" class="bk-chip"
                  :class="{ active: isOn('states', t) }" @click="toggle('states', t)">{{ $t(`backup.tradeState.${t}`) }}</button>
        </div>
      </template>
    </div>

    <div v-else class="bk-filters__row">
      <span class="bk-filters__label">{{ $t('backup.columns.kind') }}</span>
      <div class="bk-chips">
        <button v-for="k in kinds" :key="k" type="button" class="bk-chip"
                :class="{ active: isOn('kinds', k) }" @click="toggle('kinds', k)">{{ $t(`backup.kind.${k}`) }}</button>
      </div>
      <span class="bk-filters__sep"></span>
      <div class="bk-chips">
        <button v-for="r in results" :key="r" type="button" class="bk-chip bk-chip--status"
                :class="[`bk-chip--res-${r.toLowerCase()}`, { active: isOn('results', r) }]"
                @click="toggle('results', r)">{{ $t(`backup.result.${r}`) }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import mixinBackup, { BACKUP_LAST_DAY, MATCH_STATUSES } from '@/components/mixin/mixinBackup'
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox.vue'
import UserSelectBox from '@/components/widget/select/UserSelectBox.vue'

export default {
  name: 'BackupFilterBar',
  components: { OrganizationSelectBox, UserSelectBox },
  mixins: [mixinBackup],
  props: {
    value: { type: Object, required: true },
    // clients | monetary | trades | notes | log
    entity: { type: String, required: true },
    counts: { type: Object, default: null },
    since: { type: String, default: null },
  },
  data: () => ({
    customOpen: false,
    monetaryTypes: ['DEPOSIT', 'WITHDRAWAL', 'TRANSFER'],
    realTypes: ['REAL', 'FAKE'],
    kinds: ['CLIENT', 'ACCOUNT', 'FIELD', 'MONETARY', 'TRADE', 'NOTE'],
    results: ['RESTORED', 'SKIPPED', 'FAILED'],
  }),
  computed: {
    ...mapGetters('data', ['hasManyOrganizations']),
    statusOptions() {
      // CHANGED only exists for clients.
      return MATCH_STATUSES.filter(s => s !== 'CHANGED' || this.entity === 'clients')
    },
    countsTotal() {
      if (!this.counts) return null
      return Object.values(this.counts).reduce((t, n) => t + (Number(n) || 0), 0)
    },
    sinceDay() {
      return this.since ? moment(this.since).format('YYYY-MM-DD') : '2026-09-16'
    },
    days() {
      const out = []
      const d = moment(this.sinceDay, 'YYYY-MM-DD')
      const last = moment(BACKUP_LAST_DAY, 'YYYY-MM-DD')
      let guard = 0
      while (!d.isAfter(last) && guard < 31) {
        out.push({ value: d.format('YYYY-MM-DD'), label: d.format('ddd D') })
        d.add(1, 'day')
        guard += 1
      }
      return out
    },
    range() {
      return this.value.range || { from: null, until: null }
    },
    hasRange() {
      return !!(this.range.from || this.range.until)
    },
    customRange() {
      return this.hasRange && !(this.range.from && this.range.from === this.range.until)
    },
    isFiltered() {
      const v = this.value
      return !!(v.search || v.symbol || this.hasRange || v.onlyWithMissingItems
        || ['statuses', 'orgIds', 'types', 'realTypes', 'states', 'kinds', 'results', 'userIds']
          .some(k => Array.isArray(v[k]) && v[k].length))
    },
  },
  methods: {
    set(key, val) {
      this.$emit('input', { ...this.value, [key]: val })
    },
    isOn(key, val) {
      return (this.value[key] || []).includes(val)
    },
    toggle(key, val) {
      const cur = this.value[key] || []
      this.set(key, cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val])
    },
    isDay(day) {
      return this.range.from === day && this.range.until === day
    },
    setRange(from, until) {
      this.set('range', { from, until })
    },
    reset() {
      this.customOpen = false
      this.$emit('input', {})
    },
  },
}
</script>

<style scoped lang="scss">
.bk-filters {
  display: flex; flex-direction: column; gap: 0.55rem;
  padding: 0.75rem 0.85rem; margin-bottom: 0.9rem;
  border-radius: 12px; background: rgba(115, 103, 240, 0.035);
  border: 1px solid rgba(110, 117, 159, 0.14);

  &__row { display: flex; align-items: center; flex-wrap: wrap; gap: 0.5rem; }
  &__search {
    position: relative; flex: 1 1 260px; max-width: 420px;
    i { position: absolute; left: 0.7rem; top: 50%; transform: translateY(-50%); color: #9a9dad; font-size: 0.8rem; z-index: 1; }
    input { padding-left: 2rem; border-radius: 8px; }
  }
  &__symbol { width: 130px; border-radius: 8px; }
  &__org { min-width: 220px; max-width: 320px; flex: 0 1 260px; }
  &__switch { margin-left: 0.25rem; font-size: 0.85rem; font-weight: 600; }
  &__reset {
    margin-left: auto; border: none; background: transparent; color: #ea5455;
    font-size: 0.8rem; font-weight: 700; cursor: pointer; padding: 0.2rem 0.4rem; border-radius: 6px;
    &:hover { background: rgba(234, 84, 85, 0.1); }
  }
  &__label { font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; color: #9a9dad; min-width: 1.2rem; }
  &__sep { width: 1px; height: 20px; background: rgba(110, 117, 159, 0.25); margin: 0 0.15rem; }
  &__range {
    display: flex; align-items: center; gap: 0.4rem;
    input { width: 150px; border-radius: 8px; }
    i { color: #9a9dad; font-size: 0.75rem; }
  }
}

.bk-chips { display: flex; flex-wrap: wrap; gap: 0.35rem; }
.bk-chip {
  display: inline-flex; align-items: center; gap: 0.35rem;
  padding: 0.22rem 0.65rem; border-radius: 999px; cursor: pointer;
  font-size: 0.78rem; font-weight: 600; line-height: 1.4;
  color: #6e6b7b; background: #fff; border: 1px solid rgba(110, 117, 159, 0.25);
  transition: all 0.12s ease;
  i { font-size: 0.7rem; }
  &:not(.active):hover { border-color: #7367f0; color: #7367f0; }
  &.active { color: #fff; background: #7367f0; border-color: #7367f0; box-shadow: 0 4px 10px -6px rgba(115, 103, 240, 0.9); }

  &__n {
    font-size: 0.7rem; font-weight: 800; padding: 0 0.4rem; border-radius: 999px;
    background: rgba(110, 117, 159, 0.14);
  }
  &.active &__n { background: rgba(255, 255, 255, 0.25); }

  &--missing.active { background: #7367f0; border-color: #7367f0; }
  &--id_conflict.active { background: #ff9f43; border-color: #ff9f43; box-shadow: 0 4px 10px -6px rgba(255, 159, 67, 0.9); }
  &--changed.active { background: #00cfe8; border-color: #00cfe8; box-shadow: 0 4px 10px -6px rgba(0, 207, 232, 0.9); }
  &--exists.active { background: #82868b; border-color: #82868b; box-shadow: none; }
  &--restored.active, &--res-restored.active { background: #28c76f; border-color: #28c76f; box-shadow: 0 4px 10px -6px rgba(40, 199, 111, 0.9); }
  &--res-skipped.active { background: #82868b; border-color: #82868b; box-shadow: none; }
  &--res-failed.active { background: #ea5455; border-color: #ea5455; box-shadow: 0 4px 10px -6px rgba(234, 84, 85, 0.9); }
}

body.dark-layout {
  .bk-filters { background: rgba(255, 255, 255, 0.02); border-color: rgba(255, 255, 255, 0.08); }
  .bk-chip:not(.active) { background: #283046; color: #b4b7bd; border-color: #3b4253; }
  .bk-chip:not(.active):hover { color: #a39cf6; border-color: #7367f0; }
  .bk-chip.active { color: #fff; }
}
</style>
